import { filter } from "@suse-o11y/filter";
import { flatMap } from "@suse-o11y/flat-map";
import { isDeepEqual } from "@suse-o11y/is-deep-equal";
import { isPresent } from "@suse-o11y/is-present";
import { map } from "@suse-o11y/map";
import { pipe } from "@suse-o11y/pipe";
import { prop } from "@suse-o11y/prop";
import { effect, useMonitor } from "@suse-o11y/signal";
import type { FormUnit } from "@suse-o11y/signal/form";
import type { Template } from "@suse-o11y/template";
import { useTemplateVariables } from "@suse-o11y/template-variables/use-template-variables";
import { useDelayed } from "@suse-o11y/use-delayed";
import Empty from "antd/es/empty";
import React from "react";

import { SkeletonList } from "~/components/skeleton/list";
import { SkeletonText } from "~/components/skeleton/text";
import { Tag } from "~/components/tag";
import { Truncated } from "~/components/truncated";
import { Metric } from "~/models/metric";

import { type DashboardListVariableSort, sortDashboardListValues } from "../../../common/dashboard-spec";
import { type InstantPromQLResult, queryInstantPromQL } from "../../../queries/metrics/batch";
import { queryMetricsLabelValues, queryMetricsLabels } from "../../../queries/metrics/labels";
import { useQuery } from "../../../queries/react-query";
import { generateVariableNames } from "../../../routes/dashboards/dashboard/generate-variable-names";
import { GenericErrorMessage } from "../../react/error/GenericErrorMessage";
import { useFormTimeRange } from "../_form-context";

import { type FormState, Sort } from "./_form-state";

const QUERY_DEBOUNCE_MS = 300;

export interface PreviewProps {
	name: FormUnit<string, unknown, string>;
	state: FormState;
}

export const Preview: React.FC<PreviewProps> = ({ name, state }) => {
	const monitor = useMonitor();
	const templateVariables = useTemplateVariables();

	const activeSource = state.fields.source.getActiveBranch(monitor);
	const sort = state.fields.sort.getOutput(monitor) ?? Sort.None;

	if (activeSource?.kind === "StaticList") {
		return (
			<ListPreview
				sort={sort}
				state={state}
				values={activeSource.value
					.getElements(monitor)
					.map((element) => element.getOutput(monitor))
					.filter(isPresent)}
			/>
		);
	}

	const omittedVariableNames = pipe(
		[name.getInitial(monitor), name.getOutput(monitor)],
		filter(isPresent),
		flatMap(generateVariableNames),
	);

	const interpolate = (template: Template<["${", "}"], null>) => {
		return template.interpolate(templateVariables.values, {
			// do not send partially interpolated templates
			whenVariableMissing: undefined,
			// do not self-reference the variable being queried
			omit: omittedVariableNames,
		});
	};

	if (activeSource?.kind === "MetricLabelNames") {
		return (
			<MetricLabelNamesPreview
				matches={pipe(
					activeSource.value.getElements(monitor),
					map((element) => element.getOutput(monitor)),
					filter(isPresent),
					map(interpolate),
					filter(isPresent),
				)}
				sort={sort}
				state={state}
			/>
		);
	}

	if (activeSource?.kind === "MetricLabelValues") {
		return (
			<MetricLabelValuesPreview
				label={activeSource.value.fields.labelName.getOutput(monitor) ?? ""}
				matches={pipe(
					activeSource.value.fields.selectors.getElements(monitor),
					map((element) => element.getOutput(monitor)),
					filter(isPresent),
					map(interpolate),
					filter(isPresent),
				)}
				sort={sort}
				state={state}
			/>
		);
	}

	if (activeSource?.kind === "MetricPromQL") {
		const expression = activeSource.value.fields.expression.getOutput(monitor);

		return (
			<MetricsPromQLPreview
				expression={expression ? (interpolate(expression) ?? "") : ""}
				label={activeSource.value.fields.labelName.getOutput(monitor) ?? ""}
				sort={sort}
				state={state}
			/>
		);
	}

	return null;
};

const ListPreview: React.FC<{
	state: FormState;
	values: undefined | ReadonlyArray<{ value: number | string; label?: string }>;
	sort: DashboardListVariableSort;
}> = ({ state, values, sort }) => {
	const sorted = React.useMemo(() => {
		if (values == null) {
			return undefined;
		}

		const valuesWithLabels = values.map(({ value, label = value }) => ({ value, label }));

		return sortDashboardListValues(valuesWithLabels, sort);
	}, [sort, values]);

	React.useEffect(
		() =>
			effect((monitor) => {
				// If the source is changed, set the defaultValue to undefined so its
				// value can be populated when the dashboard executes the variable query
				if (state.fields.source.isDirty(monitor)) {
					state.fields.defaultValue.setInput(undefined);
				} else {
					// If there's a default value, and "Allow multiple variables" was changed
					// we should map it so it has the correct form
					const defaultValue = state.fields.defaultValue.getInput(monitor);
					if (defaultValue != null && state.fields.allowMultiple.isDirty(monitor)) {
						const allowMultiple = state.fields.allowMultiple.getOutput(monitor);

						if (defaultValue.type === "Values") {
							state.fields.defaultValue.setInput(
								allowMultiple ? defaultValue : { type: "Value", value: defaultValue.values[0] },
							);
						} else if (defaultValue.type === "Value") {
							state.fields.defaultValue.setInput(
								allowMultiple ? { type: "Values", values: [defaultValue.value] } : defaultValue,
							);
						}
					}
				}
			}),
		[state],
	);

	return (
		<SkeletonList
			className="flex flex-row flex-wrap gap-2"
			count={4}
			each={sorted}
			el="div"
			empty={<Empty className="w-full" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
		>
			{(item, _index, _arr, percent) => (
				<Tag asChild disabled style={{ width: item == null ? `${20 + 0.3 * percent}%` : undefined }}>
					<SkeletonText when={item}>
						{({ value, label }) => <Truncated tooltip={value}>{label}</Truncated>}
					</SkeletonText>
				</Tag>
			)}
		</SkeletonList>
	);
};

const MetricLabelNamesPreview: React.FC<{
	state: FormState;
	sort: DashboardListVariableSort;
	matches: ReadonlyArray<string>;
}> = ({ state, sort, matches }) => {
	const timeRange = useFormTimeRange();
	const debouncedParams = useDelayed({ matches }, QUERY_DEBOUNCE_MS, { compare: isDeepEqual });

	const { data, error } = useQuery({
		keepPreviousData: true,
		...queryMetricsLabels({ timeRange, ...debouncedParams }),
	});

	if (error) {
		return <GenericErrorMessage error={error} />;
	}

	return <ListPreview sort={sort} state={state} values={data?.map((label) => ({ id: label, value: label }))} />;
};

const MetricLabelValuesPreview: React.FC<{
	state: FormState;
	sort: DashboardListVariableSort;
	label: string;
	matches: ReadonlyArray<string>;
}> = ({ state, sort, label, matches }) => {
	const timeRange = useFormTimeRange();
	const debouncedParams = useDelayed({ label, matches }, QUERY_DEBOUNCE_MS, { compare: isDeepEqual });
	const enabled = debouncedParams.label.length > 0;

	const { data, error } = useQuery({
		enabled,
		keepPreviousData: true,
		...queryMetricsLabelValues({ timeRange, ...debouncedParams }),
	});

	if (!enabled) {
		return <ListPreview sort={sort} state={state} values={[]} />;
	}

	if (error) {
		return <GenericErrorMessage error={error} />;
	}

	return <ListPreview sort={sort} state={state} values={data?.map((value) => ({ id: value, value }))} />;
};

const MetricsPromQLPreview: React.FC<{
	state: FormState;
	sort: DashboardListVariableSort;
	expression: string;
	label: string;
}> = ({ state, sort, expression, label }) => {
	const debouncedExpression = useDelayed(expression, QUERY_DEBOUNCE_MS, { compare: isDeepEqual });
	const enabled = debouncedExpression.length > 0 && label.length > 0;

	const { error, data: options } = useQuery({
		enabled,
		keepPreviousData: true,
		refetchOnMount: false,
		refetchOnWindowFocus: false,

		select: React.useCallback(
			(result: InstantPromQLResult) =>
				result
					.transform((vectors) => Metric.extractLabelValuesByName(label, vectors.map(prop("metric"))))
					.getOrElse([])
					.map((value) => ({ value, label: value })),
			[label],
		),

		...queryInstantPromQL({ step: undefined, query: debouncedExpression }),
	});

	if (!enabled) {
		return <ListPreview sort={sort} state={state} values={[]} />;
	}

	if (error) {
		return <GenericErrorMessage error={error} />;
	}

	return <ListPreview sort={sort} state={state} values={options} />;
};
